import {
  StyledFormGroup,
  StyledInput,
  StyledLabel,
  StyledSelect,
  StyledText,
} from '@selfserve/react-ui';
import { SelectChangeEvent } from '@mui/material';
import { DatePicker, TimePicker } from '@kep-ui-kit/ui-components';
import styled from '@emotion/styled';
import { TFunction } from 'react-i18next';
import { FormikProps } from 'formik';
import { FormData } from '../feature/webinar-creator-wizard';
import { ChangeEvent, useCallback, useContext, useMemo } from 'react';
import { StyledInputFieldContainer } from '@selfserve/react-feature-create-account';
import moment from 'moment-timezone';
import { KavaEventPrefix } from '@selfserve/react-utils';
import MarketingInfoContext from '../../../../react-feature-marketing-info/src/lib/utils/markerting-info-context';
import { listTimeZones, findTimeZone, getZonedTime } from 'timezone-support';

const StyledDateTimeDurationGroup = styled(StyledFormGroup)(
  ({ theme }: { theme?: any }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    '& .picker-wrapper': {
      flexBasis: 'calc(33% - 4px)',
    },
    '& .time-picker': {
      width: '100% !important',
    },
  })
);

const StyledSelectMenu = styled(StyledSelect)(
  ({ error }: { error?: boolean }) => ({
    fontWeight: 400,
    letterSpacing: '0.00938em',
    color: '#333',
    position: 'relative',
    cursor: 'text',
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    padding: '8px',
    paddingLeft: 0,
    paddingRight: 0,
    boxSizing: 'border-box',
    borderRadius: '4px',
    lineHeight: '19px',
    height: '32px',
    ...(error && { borderColor: 'rgba(225, 36, 55, 1) !important' }),
  })
);

interface Props {
  t: TFunction<'translation', undefined>;
  formik: FormikProps<FormData>;
}

export interface TimezoneOption {
  value: string;
  text: string;
}

const additionalTimezones = [
  'US/Central',
  'US/Eastern',
  'US/Mountain',
  'US/Pacific'
];

export const WebinarDateTime = ({ t, formik }: Props) => {
  const { setFieldValue, values, touched, errors, handleBlur } = formik;
  const { raiseAnalyticEventsRequest } = useContext(MarketingInfoContext);
  const handleDateChange = useCallback(
    (value: moment.Moment) => {
      raiseAnalyticEventsRequest(`${KavaEventPrefix}DateChange`);
      setFieldValue('date', value.valueOf());
    },
    [setFieldValue]
  );

  const handleTimeChange = useCallback(
    (value: string) => {
      raiseAnalyticEventsRequest(`${KavaEventPrefix}TimeChange`);
      setFieldValue('time', value);
    },
    [setFieldValue]
  );

  const handleTimezoneChange = useCallback(
    (e: SelectChangeEvent<unknown>) => {
      raiseAnalyticEventsRequest(`${KavaEventPrefix}TimeZoneChange`);
      setFieldValue('timezone', e.target.value);
    },
    [setFieldValue]
  );

  const handleDurationChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      raiseAnalyticEventsRequest(`${KavaEventPrefix}DurationChange`);
      setFieldValue('duration', e.target.value);
    },
    [setFieldValue]
  );

  const timezonesOptions = useMemo(() => {
    const timezones = listTimeZones().concat(additionalTimezones);
    const mappedValues: Record<number, any[]> = {};
    const timezoneOffset: number[] = [];

    timezones.map((timezone) => {
      const currentTimezone = findTimeZone(timezone);

      const nativeDate = new Date();
      const currentTime = getZonedTime(nativeDate, currentTimezone);

      const offset = currentTime.zone?.offset as number;
      const offsetAbbr = currentTime.zone?.abbreviation as string;

      if (!mappedValues[offset]) {
        mappedValues[offset] = [];
        timezoneOffset.push(offset);
      }

      if (!offsetAbbr.startsWith('-') && !offsetAbbr.startsWith('+')) {
        mappedValues[offset].push({
          value: timezone,
          displayValue: `(${offsetAbbr+offset}) ${timezone}`,
        });
      }
    });

    const allTimezones: TimezoneOption[] = [];

    timezoneOffset
      .sort((timezoneA, timezoneB) => timezoneA - timezoneB)
      .forEach((offset) => {
        const timezones = mappedValues[offset].map((timezone) => ({
          value: timezone.value,
          text: timezone.displayValue,
        }));
        allTimezones.push(...timezones);
      });

    return allTimezones;
  }, []);

  const webinarDate = () => (
    <div className="picker-wrapper">
      <StyledLabel>Date</StyledLabel>
      <DatePicker
        label={t('webinar-wizard.date-label')}
        onChange={handleDateChange}
        value={values.date}
        minDate={moment()}
        inputProps={{ 'aria-label': t('aria.first-name') }}
        classes={{
          input: 'test',
        }}
      />
    </div>
  );

  const webinarTime = () => (
    <div className="picker-wrapper">
      <TimePicker
        label={t('webinar-wizard.time-label')}
        onChange={handleTimeChange}
        placeholder={values.time}
        classes={{
          container: 'time-picker',
        }}
      />
    </div>
  );
  const webinarDuration = () => (
    <div className="picker-wrapper">
      <StyledInput
        id="webinarduration"
        name="webinarDuration"
        value={values.duration}
        onChange={handleDurationChange}
        inputLabel={t('webinar-wizard.duration-label')}
        inputProps={{ 'aria-label': t('aria.first-name') }}
        type="number"
        size="small"
        classes={{
          formControl: 'date-time-flex-picker',
        }}
      />
    </div>
  );
  const webinarTimezone = () => (
    <StyledInputFieldContainer column>
      <label>{t('webinar-wizard.timezone-label')}</label>
      <StyledSelectMenu
        native
        labelId="timezone"
        name="timezone"
        className="p-dropdown"
        error={!!touched.timezone && !!errors.timezone}
        onChange={handleTimezoneChange}
        onBlur={handleBlur}
        defaultValue={values.timezone}
        inputProps={{
          'aria-label': t('aria.country-code'),
          name: 'timezone',
          id: 'timezone',
        }}
      >
        <option value="" disabled style={{ display: 'none' }}>
          {t('common.select')}
        </option>

        {timezonesOptions.map((timezone) => (
          <option key={timezone.value} value={timezone.value}>
            {timezone.text}
          </option>
        ))}
      </StyledSelectMenu>
      {!!touched.timezone && !!errors.timezone && (
        <StyledText>
          {t('webinar-wizard.validations.timezone-required')}
        </StyledText>
      )}
    </StyledInputFieldContainer>
  );

  return (
    <>
      <StyledDateTimeDurationGroup>
        {webinarDate()}
        {webinarTime()}
        {webinarDuration()}
      </StyledDateTimeDurationGroup>
      <StyledFormGroup>{webinarTimezone()}</StyledFormGroup>
    </>
  );
};
